import React, { useEffect, useState } from "react";
import { View, Text, Pressable, StyleSheet, Platform } from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { createApiClient } from "../api/apiClient";
import LhButton from "./LhButton";
import { FontFamily } from "../constants/fonts";

interface NoBasicAccessProps {
  message?: string;
  onRetry?: () => void;
  onLogout?: () => void;
}

const NoBasicAccess: React.FC<NoBasicAccessProps> = () => {
  // Resolve configuration in this order (per repo convention):
  // 1. Runtime-injected global (window.__RUNTIME_CONFIG__)
  // 2. Build-time env (.env -> process.env.REACT_APP_...)
  // 3. Empty string (no hard-coded fallback)
  const runtime = typeof window !== "undefined" ? (window as any).__RUNTIME_CONFIG__ : undefined;
  const myidmUrl = runtime?.MYIDM_URL || (process.env.REACT_APP_MYIDM_URL as string) || "";
  const pdfUrl = runtime?.NOACCESS || (process.env.REACT_APP_NOACCESS as string) || runtime?.NOACCESS_PDF || (process.env.REACT_APP_NOACCESS_PDF as string) || "";
  const pdfIdmUrl = runtime?.IDMACCESS || (process.env.REACT_APP_IDMACCESS as string) || runtime?.IDMACCESS_PDF || (process.env.REACT_APP_IDMACCESS_PDF as string) || "";
  const [resolvedPdfUrl, setResolvedPdfUrl] = useState<string>("");
  const [resolvedPdfIdmUrl, setResolvedPdfIdmUrl] = useState<string>("");

  useEffect(() => {
    let mounted = true;
    const resolvePdf = async (filename: string): Promise<string> => {
      if (/^https?:\/\//i.test(filename)) {
        return filename;
      }

      const api = createApiClient();
      const response = await api.get(
        `/common/images/url/${encodeURIComponent(filename)}?expiresInMinutes=60`
      );
      return (
        response?.data?.data?.url ??
        response?.data?.url ??
        response?.data?.data?.imageUrl ??
        response?.data?.imageUrl ??
        ""
      );
    };

    const resolve = async () => {
      const [navigatorGuide, idmGuide] = await Promise.allSettled([
        pdfUrl ? resolvePdf(pdfUrl) : Promise.resolve(""),
        pdfIdmUrl ? resolvePdf(pdfIdmUrl) : Promise.resolve(""),
      ]);

      if (!mounted) return;
      if (navigatorGuide.status === "fulfilled" && navigatorGuide.value) {
        setResolvedPdfUrl(navigatorGuide.value);
      }
      if (idmGuide.status === "fulfilled" && idmGuide.value) {
        setResolvedPdfIdmUrl(idmGuide.value);
      }
    };
    void resolve();
    return () => { mounted = false; };
  }, [pdfUrl, pdfIdmUrl]);

  const openLink = (url: string) => {
    if (typeof window !== "undefined") {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  const PdfChip: React.FC<{ name: string }> = ({ name }) => (
    <Pressable
      onPress={() => openLink(name === "myiDM Access Request Guide" ? resolvedPdfIdmUrl : resolvedPdfUrl)}
      style={({ hovered, pressed }: any) => [
        styles.pdfChip,
        Platform.OS === "web" && hovered ? styles.pdfChipHover : null,
        pressed ? styles.pdfChipHover : null,
      ]}
    >
      <View style={styles.pdfIcon}>
        <Text style={styles.pdfIconText}>PDF</Text>
      </View>
      <View>
        <Text style={styles.pdfName} numberOfLines={1}>{name}</Text>
        <Text style={styles.pdfOpen}>PREVIEW</Text>
      </View>
    </Pressable>
  );

  return (
    <View style={styles.screen} testID="no-basic-access">
      <View style={styles.card}>
        <View style={styles.iconWrap}>
          <MaterialIcons name="error-outline" size={38} color="#c12c01" />
        </View>

        <Text style={styles.title}>You do not have access to Navi yet.</Text>
        <Text style={styles.body}>
          Navi access starts with a Basic Access request in myiDM. Submit that request first, then return here to request your role.
        </Text>

        <LhButton
          text="Go to myiDM"
          size="large"
          onPress={() => openLink(myidmUrl)}
          style={styles.ctaButton}
        />

        <View style={styles.divider} />

        <View style={styles.guidesRow}>
          {resolvedPdfIdmUrl ? <PdfChip name="myiDM Access Request Guide" /> : null}
          {resolvedPdfUrl ? <PdfChip name="Navigator Access Request Guide" /> : null}
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f5f9ff",
    padding: 40,
  },
  card: {
    width: "100%",
    maxWidth: 800,
    backgroundColor: "#ffffff",
    borderRadius: 16,
    paddingVertical: 48,
    paddingHorizontal: 48,
    alignItems: "center",
    ...Platform.select({
      web: { boxShadow: "0px 8px 24px rgba(15, 29, 42, 0.08)" } as any,
      default: {
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.08,
        shadowRadius: 12,
        elevation: 3,
      },
    }),
  },
  iconWrap: {
    width: 88,
    height: 88,
    borderRadius: 44,
    backgroundColor: "#ffe5de",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 24,
  },
  title: {
    fontSize: 28,
    color: "#2d2d2d",
    textAlign: "center",
    marginBottom: 14,
    fontFamily: FontFamily.bold,
  },
  body: {
    fontSize: 15,
    lineHeight: 22,
    color: "#585858",
    textAlign: "center",
    marginBottom: 28,
    maxWidth: 560,
    fontFamily: FontFamily.regular,
  },
  ctaButton: {
    minWidth: 200,
    width: undefined,
    marginBottom: 32,
  },
  divider: {
    height: 1,
    width: "100%",
    backgroundColor: "#eef2f6",
    marginBottom: 24,
  },
  guidesRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "center",
    gap: 16,
  },
  pdfChip: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    minWidth: 260,
    maxWidth: 340,
    paddingVertical: 11,
    paddingHorizontal: 14,
    borderRadius: 8,
    backgroundColor: "#f3f7fd",
    borderWidth: 1,
    borderColor: "#daeefa",
  },
  pdfChipHover: {
    backgroundColor: "#e7f0fb",
  },
  pdfIcon: {
    width: 44,
    height: 44,
    borderRadius: 8,
    backgroundColor: "#c12c01",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  pdfIconText: {
    color: "#ffffff",
    fontSize: 12,
    fontFamily: FontFamily.bold,
  },
  pdfName: {
    fontSize: 15,
    color: "#2d2d2d",
    fontFamily: FontFamily.bold,
  },
  pdfOpen: {
    fontSize: 12,
    color: "#005cbc",
    marginTop: 2,
    fontFamily: FontFamily.bold,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
});

export default NoBasicAccess;
